import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2, Mail, KeyRound, LogIn, LogOut, Plus, Loader2, RefreshCw,
  MapPin, Calendar, Users, ArrowRight, ArrowLeft, ShieldCheck, Briefcase, Route,
  FileText, CreditCard, ExternalLink,
} from "lucide-react";
import { toast } from "sonner";
import partnerApi from "@/lib/partnerApi";
import PlacesAutocompleteInput from "@/components/PlacesAutocompleteInput";

const VEHICLE_CATEGORY = { "Comfort Class": "standard", "XL Vehicle (6+1)": "xl", "Van (8+1)": "van" };
const FIELD_CLS = "w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 outline-none transition-colors";

const STATUS_COLOR = {
  new: "bg-[#C5A059]/15 text-[#C5A059] border-[#C5A059]/30",
  contacted: "bg-blue-500/15 text-blue-300 border-blue-500/30",
  confirmed: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
  in_progress: "bg-amber-500/15 text-amber-300 border-amber-500/30",
  completed: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30",
  cancelled: "bg-red-500/15 text-red-300 border-red-500/30",
};

export default function PartnerPortal() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem("juke_partner_token");
      if (!token) { setChecking(false); return; }
      try {
        const { data } = await partnerApi.get("/partner/me");
        setMe(data);
      } catch {
        localStorage.removeItem("juke_partner_token");
      } finally {
        setChecking(false);
      }
    })();
  }, []);

  if (checking) return <FullscreenLoader />;
  if (!me) return <PartnerLogin onLogin={setMe} onBack={() => navigate("/")} />;
  return <PartnerDashboard me={me} onLogout={() => { localStorage.removeItem("juke_partner_token"); setMe(null); }} />;
}

function FullscreenLoader() {
  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center">
      <Loader2 className="w-6 h-6 text-[#C5A059] animate-spin" />
    </div>
  );
}

function PartnerLogin({ onLogin, onBack }) {
  const [email, setEmail] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !pin) return toast.error("Please enter email and PIN.");
    setBusy(true);
    try {
      const { data } = await partnerApi.post("/partner/login", { email, pin });
      localStorage.setItem("juke_partner_token", data.access_token);
      const me = await partnerApi.get("/partner/me");
      onLogin(me.data);
    } catch (err) {
      toast.error(errMsg(err) || "Invalid credentials.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="partner-login" className="min-h-screen bg-[#050505] text-zinc-100 flex flex-col">
      <button
        onClick={onBack}
        data-testid="partner-back-home"
        className="absolute top-6 left-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-zinc-500 hover:text-[#C5A059] transition-colors"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Home
      </button>

      <div className="pt-20 pb-4 text-center">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#C5A059] border border-[#C5A059]/30 rounded-full px-3 py-1">
          <Building2 className="w-3 h-3" /> B2B Portal
        </div>
        <h1 className="mt-4 font-display text-4xl sm:text-5xl text-white font-light">Hotels &amp; Agencies</h1>
        <p className="mt-3 text-sm text-zinc-400 max-w-md mx-auto">
          Sign in with your partner credentials to book transfers for your guests. All rides are billed to your account monthly.
        </p>
      </div>

      <form onSubmit={submit} className="mx-auto w-full max-w-md px-6 flex flex-col gap-5 pb-16">
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">Email</label>
          <div className="relative">
            <Mail className="w-5 h-5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="partner-email-input"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="reception@hotel.com"
              className="w-full h-14 pl-12 pr-4 rounded-xl bg-[#0d0d0d] border border-white/10 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 text-white placeholder:text-zinc-600 outline-none transition-colors"
            />
          </div>
        </div>
        <div>
          <label className="text-xs uppercase tracking-[0.2em] text-zinc-400 mb-2 block">PIN</label>
          <div className="relative">
            <KeyRound className="w-5 h-5 text-zinc-500 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              data-testid="partner-pin-input"
              type="password"
              inputMode="numeric"
              maxLength={8}
              value={pin}
              onChange={(e) => setPin(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="••••"
              className="w-full h-14 pl-12 pr-4 rounded-xl bg-[#0d0d0d] border border-white/10 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 text-2xl tracking-[0.5em] text-white placeholder:text-zinc-700 outline-none transition-colors"
            />
          </div>
        </div>
        <button
          type="submit"
          data-testid="partner-login-submit"
          disabled={busy}
          className="mt-2 h-14 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold text-lg flex items-center justify-center gap-2 transition-colors"
        >
          <LogIn className="w-5 h-5" /> {busy ? "Signing in…" : "Sign in"}
        </button>
        <p className="text-center text-xs text-zinc-500 mt-4">
          New partner? Email <a href="mailto:bookings@juketransfers.hr" className="text-[#C5A059]">bookings@juketransfers.hr</a> — we set you up within a working day.
        </p>
      </form>
    </div>
  );
}

export const errMsg = (err) => {
  const d = err?.response?.data?.detail;
  if (!d) return "";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").filter(Boolean).join(" · ");
  return JSON.stringify(d);
};

const emptyBooking = {
  name: "", email: "", phone: "",
  pickup_location: "", dropoff_location: "",
  pickup_date: "", pickup_time: "",
  passengers: 2, luggage: 0, vehicle_type: "Comfort Class", notes: "",
  booking_type: "transfer", flight_number: "",
};

function PartnerDashboard({ me, onLogout }) {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyBooking);
  const [summary, setSummary] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [pdfBusy, setPdfBusy] = useState(null);
  const isInvoice = (me.billing_type || "monthly_invoice") === "monthly_invoice";
  const [submitting, setSubmitting] = useState(false);
  const [fare, setFare] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const coordsRef = useRef({ pickup: null, dropoff: null });
  const quoteSeq = useRef(0);
  const today = new Date().toISOString().slice(0, 10);

  useEffect(() => {
    const { pickup_location, dropoff_location, vehicle_type, luggage } = form;
    if (!pickup_location?.trim() || !dropoff_location?.trim()) { setFare(null); return; }
    const seq = ++quoteSeq.current;
    setQuoting(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await partnerApi.post("/quote", {
          pickup_location,
          dropoff_location,
          pickup_coords: coordsRef.current.pickup,
          dropoff_coords: coordsRef.current.dropoff,
          vehicle_type: "Comfort Class",
          vehicle_category: VEHICLE_CATEGORY[vehicle_type] || "standard",
          pickup_time: form.pickup_time || "",
          luggage: Number(luggage) || 0,
          booking_type: "transfer",
        });
        if (seq === quoteSeq.current) setFare(data);
      } catch {
        if (seq === quoteSeq.current) setFare(null);
      } finally {
        if (seq === quoteSeq.current) setQuoting(false);
      }
    }, 600);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.pickup_location, form.dropoff_location, form.vehicle_type, form.luggage, form.pickup_time]);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await partnerApi.get("/partner/bookings");
      setBookings(data || []);
    } catch { /* soft */ }
    finally { setLoading(false); }
  };
  useEffect(() => {
    load();
    partnerApi.get("/partner/summary").then(({ data }) => setSummary(data)).catch(() => {});
    partnerApi.get("/partner/invoices").then(({ data }) => setInvoices(data || [])).catch(() => {});
  }, []);
  useEffect(() => {
    if (me.address) setForm((f) => (f.pickup_location ? f : { ...f, pickup_location: me.address }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me.address]);

  const submit = async (e) => {
    e.preventDefault();
    const missing =
      !form.name || !form.phone || !form.pickup_location || !form.dropoff_location ||
      !form.pickup_date || !form.pickup_time;
    if (missing) return toast.error("Fill in guest name, phone, pickup, drop-off, date and time.");
    setSubmitting(true);
    try {
      await partnerApi.post("/partner/bookings", {
        ...form,
        email: form.email || me.email,
        passengers: Number(form.passengers) || 1,
        luggage: Number(form.luggage) || 0,
        upsells: [],
      });
      toast.success(isInvoice
        ? "Booking confirmed — added to your monthly account."
        : "Booking confirmed — the guest pays the shown fare to the driver.");
      setForm({ ...emptyBooking, pickup_location: me.address || "" });
      await load();
    } catch (err) {
      toast.error(errMsg(err) || "Could not create the booking.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="partner-dashboard" className="min-h-screen bg-[#050505] text-zinc-100">
      <header className="border-b border-white/10 bg-[#080808]">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Building2 className="w-4 h-4 text-[#C5A059]" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-zinc-500">B2B Partner</p>
              <p data-testid="partner-company-name" className="text-sm text-white font-medium">{me.company_name}</p>
            </div>
            <span data-testid="partner-billing-badge" className={`ml-3 hidden sm:inline text-[9px] uppercase tracking-[0.2em] px-2.5 py-1 rounded-full border ${isInvoice ? "border-[#C5A059]/50 text-[#C5A059]" : "border-emerald-400/50 text-emerald-300"}`}>
              {isInvoice ? "Monthly Account Invoicing" : "Guest Pays Driver in Vehicle"}
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={load} data-testid="partner-refresh" aria-label="Refresh" className="p-2 text-zinc-400 hover:text-[#C5A059]">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
            <button onClick={onLogout} data-testid="partner-logout" aria-label="Log out" className="p-2 text-zinc-400 hover:text-[#C5A059]">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-10">
        <form onSubmit={submit} data-testid="partner-booking-form" className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-6 space-y-3 h-fit lg:sticky lg:top-6">
          <div className="flex items-center gap-2 text-sm text-white mb-1">
            <Plus className="w-4 h-4 text-[#C5A059]" />
            <span className="font-medium">New transfer for a guest</span>
          </div>
          <Row>
            <Text label="Guest name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} placeholder="Mr. Smith · Room 404" data-testid="partner-form-name" />
          </Row>
          <Row>
            <Text label="Guest phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} placeholder="+44 …" data-testid="partner-form-phone" />
            <Text label="Guest email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} placeholder="optional" data-testid="partner-form-email" />
          </Row>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Pickup</label>
            <PlacesAutocompleteInput
              testId="partner-form-pickup"
              value={form.pickup_location}
              onChange={(v) => { coordsRef.current.pickup = null; setForm((f) => ({ ...f, pickup_location: v })); }}
              onPlaceSelected={({ name, lat, lon }) => { coordsRef.current.pickup = { lat, lon }; setForm((f) => ({ ...f, pickup_location: name })); }}
              placeholder="Hotel / villa / address"
              className={FIELD_CLS}
            />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Drop-off</label>
            <PlacesAutocompleteInput
              testId="partner-form-dropoff"
              value={form.dropoff_location}
              onChange={(v) => { coordsRef.current.dropoff = null; setForm((f) => ({ ...f, dropoff_location: v })); }}
              onPlaceSelected={({ name, lat, lon }) => { coordsRef.current.dropoff = { lat, lon }; setForm((f) => ({ ...f, dropoff_location: name })); }}
              placeholder="Split Airport, Dubrovnik…"
              className={FIELD_CLS}
            />
          </div>
          <Row>
            <Text type="date" min={today} label="Date" value={form.pickup_date} onChange={(v) => setForm({ ...form, pickup_date: v })} data-testid="partner-form-date" />
            <Text type="time" label="Time" value={form.pickup_time} onChange={(v) => setForm({ ...form, pickup_time: v })} data-testid="partner-form-time" />
          </Row>
          <Row>
            <Text type="number" min="1" max="8" label="Passengers" value={form.passengers} onChange={(v) => setForm({ ...form, passengers: v })} data-testid="partner-form-pax" />
            <Text type="number" min="0" max="15" label="Suitcases" value={form.luggage} onChange={(v) => setForm({ ...form, luggage: v })} data-testid="partner-form-luggage" />
          </Row>
          <Row>
            <Text label="Flight number" value={form.flight_number} onChange={(v) => setForm({ ...form, flight_number: v.toUpperCase() })} placeholder="Optional (for airport pickups)" data-testid="partner-form-flight" />
            <div>
              <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Vehicle type</label>
              <select
                data-testid="partner-form-vehicle"
                value={form.vehicle_type}
                onChange={(e) => setForm({ ...form, vehicle_type: e.target.value })}
                className="w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white focus:border-[#C5A059] outline-none"
              >
                <option value="Comfort Class">Comfort (up to 4)</option>
                <option value="XL Vehicle (6+1)">XL Vehicle (up to 6)</option>
                <option value="Van (8+1)">Van (up to 8)</option>
              </select>
            </div>
          </Row>
          <div>
            <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">Notes</label>
            <textarea
              data-testid="partner-form-notes"
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Child seat, meeting point, special instructions…"
              rows={2}
              className="w-full px-3 py-2 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 outline-none"
            />
          </div>

          {(quoting || fare) && (
            <div data-testid="partner-fare-preview" className="rounded-xl border border-[#C5A059]/30 bg-[#C5A059]/[0.06] px-4 py-3">
              {quoting ? (
                <div className="flex items-center gap-2 text-xs text-zinc-400">
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[#C5A059]" /> Calculating fare…
                </div>
              ) : (
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-400">
                    <Route className="w-3.5 h-3.5 text-[#C5A059]" />
                    <span>{Number(fare.distance_km).toFixed(0)} km · ~{fare.duration_min} min{fare.night_surcharge ? " · night" : ""}</span>
                  </div>
                  <p data-testid="partner-fare-amount" className="text-xl font-display text-[#C5A059] tabular-nums">
                    €{Number(fare.total_price_eur).toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          )}

          <div data-testid="partner-billing-banner" className={`pt-2 flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] ${isInvoice ? "text-zinc-500" : "text-emerald-400"}`}>
            <ShieldCheck className="w-3 h-3" />
            {isInvoice
              ? `Cashless · Billed to ${me.company_name} monthly — guest pays €0.00 in the vehicle`
              : "Guest pays the exact fare to the driver in the vehicle"}
          </div>

          <button
            type="submit"
            data-testid="partner-form-submit"
            disabled={submitting}
            className="w-full h-12 rounded-xl bg-[#C5A059] hover:bg-[#D4AF37] disabled:opacity-60 text-black font-semibold text-sm uppercase tracking-[0.15em] transition-colors"
          >
            {submitting ? "Sending…" : "Book transfer"}
          </button>
        </form>

        <section>
          {summary && (
            <div data-testid="partner-monthly-summary" className="mb-5 rounded-2xl border border-[#C5A059]/25 bg-[#C5A059]/[0.05] p-4 flex flex-wrap items-center gap-6">
              <div>
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">This month · {summary.month}</p>
                <p className="text-2xl font-display text-white font-light tabular-nums mt-0.5">{summary.completed_rides} <span className="text-sm text-zinc-400">completed rides</span></p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">{isInvoice ? "Accrued to monthly account" : "Total ride value"}</p>
                <p className="text-2xl font-display text-[#C5A059] tabular-nums mt-0.5">€{Number(summary.completed_value_eur || 0).toFixed(2)}</p>
              </div>
            </div>
          )}
          {invoices.length > 0 && (
            <div className="mb-8" data-testid="partner-invoices-section">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="w-4 h-4 text-[#C5A059]" />
                <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-400">Invoices · Računi</h2>
              </div>
              <div className="space-y-3">
                {invoices.map((inv) => {
                  const paid = (inv.status || "").startsWith("PLAĆENO");
                  return (
                    <article key={inv.id} data-testid={`partner-invoice-${inv.id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4 sm:p-5">
                      <div className="flex flex-wrap items-center gap-3">
                        <div className="min-w-0 flex-1">
                          <p className="text-sm text-white">
                            Račun {inv.invoice_number}
                            <span
                              data-testid={`partner-invoice-status-${inv.id}`}
                              className={`ml-2 text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${paid ? "border-emerald-400/50 text-emerald-300" : "border-amber-400/50 text-amber-300"}`}
                            >
                              {inv.status}
                            </span>
                          </p>
                          <p className="text-xs text-zinc-500 mt-1">
                            {inv.period_start} – {inv.period_end} · {inv.ride_count} rides · due {inv.due_date}
                          </p>
                        </div>
                        <p className="text-xl font-display text-[#C5A059] tabular-nums">€{Number(inv.gross_eur || 0).toFixed(2)}</p>
                        <div className="flex items-center gap-2">
                          {!paid && inv.stripe_payment_url && (
                            <a
                              href={inv.stripe_payment_url} target="_blank" rel="noreferrer"
                              data-testid={`partner-invoice-pay-${inv.id}`}
                              className="h-9 px-4 rounded-full bg-[#C5A059] hover:bg-[#D4AF37] text-black text-[10px] uppercase tracking-[0.15em] font-semibold flex items-center gap-1.5 transition-colors"
                            >
                              <CreditCard className="w-3.5 h-3.5" /> Pay online
                            </a>
                          )}
                          <button
                            data-testid={`partner-invoice-pdf-${inv.id}`}
                            disabled={pdfBusy === inv.id}
                            onClick={async () => {
                              setPdfBusy(inv.id);
                              try {
                                const { data } = await partnerApi.get(`/partner/invoices/${inv.id}/pdf`, { responseType: "blob" });
                                window.open(URL.createObjectURL(new Blob([data], { type: "application/pdf" })), "_blank");
                              } catch { toast.error("PDF unavailable"); }
                              finally { setPdfBusy(null); }
                            }}
                            className="h-9 px-4 rounded-full border border-white/15 hover:border-[#C5A059] text-zinc-300 text-[10px] uppercase tracking-[0.15em] flex items-center gap-1.5 transition-colors"
                          >
                            <ExternalLink className="w-3.5 h-3.5" /> PDF
                          </button>
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          )}

          <div className="flex items-center gap-2 mb-4">
            <Briefcase className="w-4 h-4 text-[#C5A059]" />
            <h2 className="text-sm uppercase tracking-[0.2em] text-zinc-400">Your recent bookings</h2>
          </div>
          {loading && <p className="text-zinc-500 text-sm">Loading…</p>}
          {!loading && bookings.length === 0 && (
            <p className="text-sm text-zinc-500">No bookings yet. Create your first transfer on the left.</p>
          )}
          <div className="space-y-3">
            {bookings.map((b) => (
              <article key={b.id} data-testid={`partner-booking-${b.id}`} className="rounded-2xl border border-white/10 bg-[#0d0d0d] p-4 sm:p-5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="w-3.5 h-3.5 text-[#C5A059]" />
                      <span className="text-sm text-white">{b.pickup_date} · {b.pickup_time}</span>
                      <span className={`text-[9px] uppercase tracking-[0.2em] px-2 py-0.5 rounded-full border ${STATUS_COLOR[b.status] || STATUS_COLOR.new}`}>
                        {b.status}
                      </span>
                    </div>
                    <p className="text-white font-medium truncate">{b.name}</p>
                    <p className="text-xs text-zinc-500 mt-0.5">{b.ref} · {b.passengers} pax</p>
                  </div>
                  {b.quote_amount > 0 && (
                    <div className="text-right shrink-0">
                      <p className="text-lg font-display text-[#C5A059] tabular-nums">€{Number(b.quote_amount).toFixed(2)}</p>
                      <p className={`text-[9px] uppercase tracking-widest ${b.collect_from_guest ? "text-emerald-400" : "text-zinc-500"}`}>
                        {b.collect_from_guest ? "guest pays driver" : "on monthly account · guest pays €0.00"}
                      </p>
                    </div>
                  )}
                </div>
                {b.driver_info && (
                  <p data-testid={`partner-driver-${b.id}`} className="mt-2 text-xs text-[#C5A059]">
                    🚘 Driver: {b.driver_info.name}{b.driver_info.phone ? ` · ${b.driver_info.phone}` : ""}{b.driver_info.plate ? ` · ${b.driver_info.plate}` : ""}
                  </p>
                )}
                <div className="mt-3 text-xs text-zinc-400 flex items-start gap-2">
                  <MapPin className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
                  <span>{b.pickup_location}</span>
                  <ArrowRight className="w-3 h-3 text-zinc-600 shrink-0 mt-0.5" />
                  <span>{b.dropoff_location || "—"}</span>
                </div>
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}

function Row({ children }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Text({ label, value, onChange, ...rest }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5 block">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-11 px-3 bg-[#050505] border border-white/10 rounded-lg text-sm text-white placeholder:text-zinc-600 focus:border-[#C5A059] focus:ring-1 focus:ring-[#C5A059]/40 outline-none transition-colors"
        {...rest}
      />
    </div>
  );
}
